import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ajvResolver, ResponseError, responseErrorsResolver } from '@ui/src';
import { CofProps, NewCofFormValues } from '../../types';
import {
  useAddCorporateCOFMutation,
  useLazyGetCorporateCOFQuery,
} from '../../store/services/corporate';
import { useLazyGetCustomersApplicationSchemesQuery } from '../../../../store/dictionaries/services';
import { NatureOfControlEnum } from '../../../new-application/types';
import {
  SignatureType,
  AccessRight,
  ApplicationScheme,
} from '../../../common/types';

const schema = {
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'First Name is required',
    },
    lastName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Last Name is required',
    },
    email: {
      type: 'string',
      minLength: 1,
      errorMessage: 'E-mail is required',
    },
    phone: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Phone is required',
    },
    position: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Position is required',
    },
    nationalityId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Nationality is required',
    },
    dob: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Date of Birth is required',
    },
    signatureType: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Signature type is required',
    },
    accessRight: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Access Right type is required',
    },
  },
  required: [
    'firstName',
    'lastName',
    'email',
    'phone',
    'dob',
    'position',
    'signatureType',
    'accessRight',
    'nationalityId',
  ],
};

const useCreateCompanyOfficial = ({
  customerId,
  applicationSchemes,
}: {
  customerId?: string;
  applicationSchemes?: ApplicationScheme[];
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [cofError, setCofError] = useState('');
  const [sumSubError, setSumSubError] = useState('');

  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<NewCofFormValues>({
    defaultValues: {
      havingControl: false,
    },
    resolver: ajvResolver(schema),
  });

  const havingControl = watch('havingControl');
  const percentage = watch('percentage');

  const [addNewCof, { isLoading: addNewCofInProgress, isSuccess }] =
    useAddCorporateCOFMutation();

  const [
    getCorporateCof,
    { isLoading: cofIsLoading, isSuccess: cofLoadingIsSuccess },
  ] = useLazyGetCorporateCOFQuery();

  const [getCustomersApplicationSchemes] =
    useLazyGetCustomersApplicationSchemesQuery();

  const handleClose = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(-1);
    }, 100);
  };

  const onSubmit = handleSubmit(async (values) => {
    setSumSubError('');
    setCofError('');

    const dob = new Date(values.dob);

    const cofBody: {
      email: string;
      firstName: string;
      lastName: string;
      phone: string;
      organizationId: string;
      position: string;
      companyOfficialType: string;
      nationalityId: string;
      signatureType: SignatureType;
      dob: string;
      percentage?: string;
      natureOfControl?: NatureOfControlEnum;
      applicantId?: string;
      accessRight: AccessRight;
      applicationSchemeId?: string;
    } = {
      email: values?.email,
      firstName: values?.firstName,
      lastName: values?.lastName,
      phone: values?.phone,
      organizationId: customerId as string,
      position: values?.position,
      companyOfficialType: values?.companyOfficialType || 'COMPANY_OFFICIAL',
      signatureType: values?.signatureType as SignatureType,
      dob: `${dob.getFullYear()}-${(dob.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${dob.getDate().toString().padStart(2, '0')}`,
      accessRight: values.accessRight || AccessRight.VIEWER,
      nationalityId: values.nationalityId,
    };

    if (values.applicationSchemeId) {
      cofBody.applicationSchemeId = values.applicationSchemeId;
    }

    if (values.applicantId) {
      cofBody.applicantId = values.applicantId;
    }

    if (havingControl) {
      cofBody.percentage = values.percentage;
      cofBody.natureOfControl = values.natureOfControl as NatureOfControlEnum;
    }

    const response = (await addNewCof(cofBody)) as {
      data?: { data: CofProps };
      error?: ResponseError | { data: { message: string } };
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error as ResponseError,
        setError,
        setGlobalError: setCofError,
      });

      if (response.error.data && response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0] &&
          typeof response.error.data.message[0] === 'string' &&
          (response.error.data.message[0] as string).includes('applicant')
        ) {
          setSumSubError(response.error.data.message[0]);
        }
      }
    }
  });

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (isSuccess && customerId) {
      getCorporateCof(customerId);
    }
  }, [isSuccess, customerId]);

  useEffect(() => {
    if (cofLoadingIsSuccess) {
      handleClose();
    }
  }, [cofLoadingIsSuccess]);

  useEffect(() => {
    if (percentage && !/^\d*\.?\d*$/.test(percentage)) {
      setError(
        'percentage',
        { message: 'Percentage of control field must be numeric' },
        { shouldFocus: true },
      );
    } else {
      clearErrors('percentage');
    }
  }, [percentage]);

  useEffect(() => {
    if (!applicationSchemes) {
      getCustomersApplicationSchemes();
    }
  }, [applicationSchemes]);

  return {
    panelIsOpen,
    handleClose,
    addNewCofInProgress,
    cofIsLoading,
    control,
    havingControl,
    errors,
    cofError,
    sumSubError,
    onSubmit,
  };
};

export default useCreateCompanyOfficial;
